const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// API Response wrapper
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  errorCode?: string;
}

async function unwrapResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.error || `API error: ${response.status}`);
  }

  const json: ApiResponse<T> = await response.json();
  if (!json.success) {
    throw new Error(json.error || 'Unknown error');
  }

  return json.data as T;
}

export interface SourceReference {
  url: string;
  title: string;
  snippet: string;
  citationIndex: number;
}

export interface ChatResponse {
  answer: string;
  sources: SourceReference[];
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface StatsResponse {
  queryCount: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  sessionCount: number;
}

// Claude 3.5 Haiku pricing per 1M tokens
const INPUT_PRICE_PER_MILLION = 0.80;
const OUTPUT_PRICE_PER_MILLION = 4.00;

export function calculateCost(inputTokens: number, outputTokens: number): number {
  return (inputTokens * INPUT_PRICE_PER_MILLION / 1_000_000) +
         (outputTokens * OUTPUT_PRICE_PER_MILLION / 1_000_000);
}

export async function sendMessage(message: string, history: ChatMessage[], sessionId?: string): Promise<ChatResponse> {
  const response = await fetch(`${API_URL}/api/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, sessionId, history }),
  });

  return unwrapResponse<ChatResponse>(response);
}

export interface TopicCount {
  category: string;
  count: number;
}

export interface LogEntry {
  id: number;
  question: string;
  category: string | null;
  answerQuality: string | null;
  language: string | null;
  searchScore: number | null;
  chunksFound: number | null;
  inputTokens: number;
  outputTokens: number;
  responseTimeMs: number | null;
  isFollowup: boolean | null;
  createdAt: string;
}

export interface PagedLogsResponse {
  items: LogEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export async function getStats(from: string, to: string): Promise<StatsResponse> {
  const response = await fetch(`${API_URL}/api/stats?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
  return unwrapResponse<StatsResponse>(response);
}

export async function getLastUpdate(): Promise<string | null> {
  const response = await fetch(`${API_URL}/api/stats/last-update`);
  return unwrapResponse<string | null>(response);
}

export async function getLogs(from: string, to: string, page: number = 1, pageSize: number = 20): Promise<PagedLogsResponse> {
  const response = await fetch(`${API_URL}/api/stats/logs?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&page=${page}&pageSize=${pageSize}`);
  return unwrapResponse<PagedLogsResponse>(response);
}

export async function getTopics(from: string, to: string): Promise<TopicCount[]> {
  const response = await fetch(`${API_URL}/api/stats/topics?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
  return unwrapResponse<TopicCount[]>(response);
}
